import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { AbstractControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { BehaviorSubject, Observable, ReplaySubject, forkJoin, of, defer } from 'rxjs';
import { filter, first, map, tap, finalize, shareReplay, distinctUntilChanged, catchError } from 'rxjs/operators';

import { ConfigCountry } from 'src/app/domain/ConfigCountry';
import { CustomerService } from 'src/app/services/customer.service';
import { ProductService } from 'src/app/services/product.service';
import { OrderService } from 'src/app/services/order.service';
import { ListServiceResponse, ServiceService } from 'src/app/services/service.service';
import { CommonResponse, PageService } from 'src/app/services/page.service';
import { ShopService, ShopConfiguration } from 'src/app/services/shop.service';
import { ApiCountryConfig, CountryService } from 'src/app/services/country.service';
import { OverlaysService } from './overlays.service';
import { StorageService } from 'src/app/services/storage.service';
import { ValidationsCountry } from '../shared/interfaces/country-validation.interface';
import { CustomerAddressTypeEnum } from '../shared/enums/customer-address-type-enum';
import { FrontLinkTypeEnum } from '../shared/enums/front-link-type-enum';
import { ProductStatusEnum } from '../shared/enums/product-status-enum';
import { PaymentMethodCodeEnum } from '../shared/enums/payment-method-code-enum';
import { GlovoWalletModalComponent } from '../shared/modals/glovo-wallet-modal/glovo-wallet-modal.component';

import { FrontLink } from '../domain/FrontLink';
import { Group, Service } from '../domain/Service';
import { Product } from '../domain/Product';
import { Order } from '../domain/Order';
import { StarredProductList } from '../domain/StarredProductList';
import { Landing } from '../domain/Landing';
import { Country, LanguageCountry } from '../domain/Country';
import { Customer } from '../domain/Customer';
import { UpperBanner } from '../domain/UpperBanner';

import { ecommerceConstants } from 'src/app/shared/ecommerce-constants';
import { environment } from 'src/environments/environment';

import { countryConfigES } from 'src/app/shared/config/ES';
import { countryConfigGE } from 'src/app/shared/config/GE';
import { countryConfigHR } from 'src/app/shared/config/HR';
import { countryConfigIT } from 'src/app/shared/config/IT';
import { countryConfigKZ } from 'src/app/shared/config/KZ';
import { countryConfigMA } from 'src/app/shared/config/MA';
import { countryConfigPL } from 'src/app/shared/config/PL';
import { countryConfigPT } from 'src/app/shared/config/PT';
import { countryConfigRO } from 'src/app/shared/config/RO';
import { countryConfigRS } from 'src/app/shared/config/RS';
import { countryConfigKG } from 'src/app/shared/config/KG';
import { countryConfigUA } from 'src/app/shared/config/UA';
import { countryConfigSI } from 'src/app/shared/config/SI';
import { countryConfigBG } from 'src/app/shared/config/BG';
import { countryConfigBA } from 'src/app/shared/config/BA';
import { countryConfigME } from 'src/app/shared/config/ME';
import { countryConfigCI } from 'src/app/shared/config/CI';
import { countryConfigAD } from 'src/app/shared/config/AD';
import { countryConfigAM } from 'src/app/shared/config/AM';
import { countryConfigKE } from 'src/app/shared/config/KE';
import { countryConfigGH } from 'src/app/shared/config/GH';
import { countryConfigMD } from 'src/app/shared/config/MD';
import { PaymentIcon } from '../domain/PaymentMethod';
import { PaymentMethodCountry } from '../domain/PaymentMethodCountry';

export interface GlobalParams {
  shopConfiguration?: ShopConfiguration;
  config?: ConfigCountry;
  country?: Country;
  validations?: ValidationsCountry;
  language?: LanguageCountry;
  services?: Service[];
  landings?: Landing[];
}

@Injectable({
  providedIn: 'root',
})
export class GlobalService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);
  private readonly customerService = inject(CustomerService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly productService = inject(ProductService);
  private readonly orderService = inject(OrderService);
  private readonly shopService = inject(ShopService);
  private readonly countryService = inject(CountryService);
  private readonly pageService = inject(PageService);
  private readonly serviceService = inject(ServiceService);
  private readonly overlaysService = inject(OverlaysService);
  private readonly modalService = inject(NgbModal);
  private readonly storageService = inject(StorageService);

  private paramsSubject$: BehaviorSubject<GlobalParams>;
  public params$: Observable<GlobalParams>;

  // IntersectionObserver-based visibility tracking — avoids getBoundingClientRect() reflows on scroll
  private visibilityMap = new Map<string, boolean>();
  private visibilityObserver: IntersectionObserver | null = null;

  private isShopConfigLoading = false;
  private readonly loadedCountryConfigs = new Set<string>();
  private readonly loadedCountryValidations = new Set<string>();
  private readonly loadedCountryApiConfigs = new Set<string>();
  private readonly loadedOrderConfigs = new Set<string>();

  private miniCartLoadingSubject = new BehaviorSubject<boolean>(false);
  miniCartLoading$ = this.miniCartLoadingSubject.asObservable();

  private readonly activeMiniCartSubject$: BehaviorSubject<boolean>;
  public activeMiniCart$: Observable<boolean>;


  private lastUpperBannerCountryCode?: string;
  private lastUpperBannerRoleKey?: string;

  private readonly routeDataChangeSource = new ReplaySubject<Data>();

  public previousUrl: string;
  public currentUrl: string;

  private readonly PAYMENT_ICON_MAP_FOOTER = new Map<PaymentMethodCodeEnum, { srcs: string[]; alt: string, width: string, height: string }>([
    [PaymentMethodCodeEnum.card, { srcs: ['/assets/img/footer-visa.svg', '/assets/img/footer-mastercard.svg'], alt: 'Card', width: '70', height: '28' }],
    [PaymentMethodCodeEnum.token, { srcs: ['/assets/img/footer-visa.svg', '/assets/img/footer-mastercard.svg'], alt: 'Token', width: '70', height: '28' }],
    [PaymentMethodCodeEnum.paypal, { srcs: ['/assets/img/footer-paypal.svg'], alt: 'PayPal', width: '70', height: '28' }],
    [PaymentMethodCodeEnum.applePay, { srcs: ['/assets/img/footer-apple-pay.svg'], alt: 'Apple Pay', width: '70', height: '28' }],
    [PaymentMethodCodeEnum.googlePay, { srcs: ['/assets/img/footer-google-pay.svg'], alt: 'Google Pay', width: '70', height: '28' }],
    [PaymentMethodCodeEnum.blik, { srcs: ['/assets/img/footer-blik.svg'], alt: 'BLIK', width: '70', height: '28' }],
    [PaymentMethodCodeEnum.mbway, { srcs: ['/assets/img/footer-mbway.svg'], alt: 'MB WAY', width: '70', height: '28' }],
    [PaymentMethodCodeEnum.dotPay, { srcs: ['/assets/img/footer-dotpay.svg'], alt: 'Dot Pay', width: '70', height: '28' }],
    [PaymentMethodCodeEnum.cellulant, { srcs: ['/assets/img/footer-cellulant.svg'], alt: 'Cellulant', width: '70', height: '28' }],
  ]);

  private readonly PAYMENT_ICON_MAP_CART = new Map<PaymentMethodCodeEnum, { srcs: string[]; alt: string, width: string, height: string }>([
    [PaymentMethodCodeEnum.card, { srcs: ['/assets/img/cart-visa.svg', '/assets/img/cart-mastercard.svg'], alt: 'Card', width: '50', height: '28' }],
    [PaymentMethodCodeEnum.token, { srcs: ['/assets/img/cart-visa.svg', '/assets/img/cart-mastercard.svg'], alt: 'Token', width: '50', height: '28' }],
    [PaymentMethodCodeEnum.paypal, { srcs: ['/assets/img/cart-paypal.svg'], alt: 'PayPal', width: '50', height: '28' }],
    [PaymentMethodCodeEnum.applePay, { srcs: ['/assets/img/cart-apple-pay.svg'], alt: 'Apple Pay', width: '50', height: '28' }],
    [PaymentMethodCodeEnum.googlePay, { srcs: ['/assets/img/cart-google-pay.svg'], alt: 'Google Pay', width: '50', height: '28' }],
    [PaymentMethodCodeEnum.blik, { srcs: ['/assets/img/cart-blik.svg'], alt: 'BLIK', width: '50', height: '28' }],
    [PaymentMethodCodeEnum.mbway, { srcs: ['/assets/img/cart-mbway.svg'], alt: 'MB WAY', width: '50', height: '28' }],
    [PaymentMethodCodeEnum.cellulant, { srcs: ['/assets/img/cart-cellulant.svg'], alt: 'Cellulant', width: '50', height: '28' }],
  ]);

  constructor() {
    const router = this.router;

    this.initParams();

    this.activeMiniCartSubject$ = new BehaviorSubject(false);
    this.activeMiniCart$ = this.activeMiniCartSubject$.asObservable();

    this.paramsSubject$
      .pipe(filter((p) => p != null))
      .subscribe((params) => {
        this.storageService.setItem('params', params);
      });

    this.customerService.loggedCustomer$
      .pipe(
        map(() => this.getRoleKey()),
        distinctUntilChanged()
      )
      .subscribe(() => {
        const current = this.paramsSubject$.getValue();
        if (!current) return;
        this.lastUpperBannerRoleKey = undefined;

        this.setUpperBanner(current).subscribe({
          next: () => this.paramsSubject$.next({ ...current }),
          error: () => this.paramsSubject$.next({ ...current }),
        });
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      ).subscribe(() => {
        let r = this.route;
        while (r.firstChild) {
          r = r.firstChild;
        }
        r.data.pipe(first()).subscribe((data: Data) => {
          this.routeDataChangeSource.next(data);
        });
    });

    this.currentUrl = this.router.url;
    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
      }
    });
  }

  get currentParams(): GlobalParams {
    return this.storageService.getItem<GlobalParams>('params');
  }

  private initParams() {
    const initial: GlobalParams = this.currentParams ?? {};
    const lsCfg = this.storageService.getItem<ShopConfiguration>('shopConfiguration');
    if (lsCfg) {
      if (!initial.shopConfiguration || initial.shopConfiguration.version !== lsCfg.version) {
        initial.shopConfiguration = lsCfg;
      }
    }

    if (initial.shopConfiguration) {
      this.applyThemeColors(initial.shopConfiguration);
    }

    this.paramsSubject$ = new BehaviorSubject<GlobalParams | null>(initial);
    this.params$ = this.paramsSubject$.pipe(
      filter((p): p is GlobalParams => p != null),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  setParams(params: any) {
    const currentParams = this.paramsSubject$.getValue() ?? {};

    if (params.countryCode) {
      const basic = this.countryService.getCountryByCode(params.countryCode);
      if (basic) {
        const isChangingCountry = this.paramsSubject$.getValue()?.country?.code !== basic.code;
        currentParams.country = isChangingCountry ? { ...basic } : { ...(currentParams.country ?? {}), ...basic };

        const staticCfg = this.getCountryConfig(basic.code) as ConfigCountry;
        currentParams.config = isChangingCountry ? ((staticCfg ?? {}) as any) : (currentParams.config ?? ((staticCfg ?? {}) as any));
        if (!currentParams.config.home) {
          currentParams.config.home = (staticCfg?.home ?? {}) as any;
        }

        const bootstrapParams: GlobalParams = {
          ...(this.paramsSubject$.getValue() ?? {}),
          ...currentParams,
        };
        this.storageService.setCurrentCountry(basic.code);
        this.storageService.setItem('params', bootstrapParams);
        this.storageService.setGlobalItem('lastCountryVisited', {
          code: basic.code,
          locale: basic.locale,
        });
        this.orderService.reloadFromStorage();
      }
    }

    if (params.locale && this.translateService.currentLang !== params.locale) {
      this.translateService.use(params.locale);
    }

    this.productService.setCategories(currentParams?.country?.code);

    if (!currentParams.shopConfiguration) {
      const lsCfgStr = this.storageService.getItem<ShopConfiguration>('shopConfiguration');
      if (lsCfgStr) {
        currentParams.shopConfiguration = lsCfgStr;
        this.applyThemeColors(currentParams.shopConfiguration);
      }
    }

    const tasks: Observable<unknown>[] = [];
    tasks.push(this.setShopConfiguration(currentParams));
    tasks.push(this.setUpperBanner(currentParams));
    tasks.push(this.setApiConfig(currentParams));
    tasks.push(this.setApiCountry(currentParams));
    tasks.push(this.setApiValidations(currentParams));
    tasks.push(this.setOrderConfigs(currentParams));

    forkJoin(tasks).subscribe({
      next: () => this.paramsSubject$.next({ ...currentParams }),
      error: () => this.paramsSubject$.next({ ...currentParams }),
    });
  }

  isAvailableCountryCode(countryCode: string) {
    return this.countryService.getCountryByCode(countryCode);
  }

  private setShopConfiguration(params: GlobalParams): Observable<void> {
    if (this.isShopConfigLoading) {
      return of(void 0);
    }

    this.isShopConfigLoading = true;

    return this.shopService.getShopConfiguration().pipe(
      tap((cfg: ShopConfiguration) => {
        const lsCfg = this.storageService.getItem<ShopConfiguration>('shopConfiguration');
        const storedVersion = lsCfg?.version ?? null;

        if (storedVersion === null || storedVersion !== cfg.version) {
          this.storageService.setItem('shopConfiguration', cfg);
          params.shopConfiguration = cfg;
          this.applyThemeColors(cfg);
        }
      }),
      finalize(() => { this.isShopConfigLoading = false; }),
      map(() => void 0)
    );
  }

  private upperBannerKeyForRole(roleKey: string): string {
    return `upperBanner:${roleKey}`;
  }

  private getRoleKey(): string {
    return this.customerService.currentCustomer?.role?.toString() ?? 'guest';
  }

  /**
   * UpperBanner independiente de /common.
   * - Cache en localStorage por país (StorageService) + por rol (key distinta).
   * - Se vuelve a pedir cuando cambie país o rol.
   */
  private setUpperBanner(params: GlobalParams): Observable<void> {
    const countryCode = params?.country?.code;
    if (!countryCode) return of(void 0);

    const roleKey = this.getRoleKey();

    // evita duplicados por país+rol
    if (this.lastUpperBannerCountryCode === countryCode && this.lastUpperBannerRoleKey === roleKey) {
      return of(void 0);
    }
    this.lastUpperBannerCountryCode = countryCode;
    this.lastUpperBannerRoleKey = roleKey;

    const storageKey = this.upperBannerKeyForRole(roleKey);

    // 1) Pinta cache inmediata (si existe) o limpia para evitar "flash" incorrecto
    const cached = this.storageService.getItem<UpperBanner | null>(storageKey, { countryCode });
    params.config = params.config ?? ({} as ConfigCountry);
    params.config.upperBanner = cached ?? null;

    // 2) Pide al back y actualiza + persiste
    return this.pageService.getUpperBanner().pipe(
      tap((upperBanner) => {
        params.config = params.config ?? ({} as ConfigCountry);
        params.config.upperBanner = upperBanner ?? null;
        this.storageService.setItem(storageKey, upperBanner ?? null, { countryCode });
      }),
      catchError((e) => {
        console.error('upper-banner error', e);
        return of(null);
      }),
      map(() => void 0)
    );
  }

  private setApiConfig(params: GlobalParams): Observable<void> {
    const code = params?.country?.code;
    if (!code || this.loadedCountryApiConfigs.has(code)) {
      return of(void 0);
    }
    this.loadedCountryApiConfigs.add(code);

    const conf$ = this.pageService.common().pipe(
      tap((x: CommonResponse) => {
        params.config.common = {
          ...params.config.common,
          headerLinks:  x.headerLinks   ?? [],
          subheader:    x.subheader     ?? null,
          benefitsBars: x.benefitsBars  ?? [],
          footerSections: x.footerSections ?? []
        };
      }),
      map(() => void 0)
    );

    const services$ = this.serviceService.list().pipe(
        tap((x: ListServiceResponse) => {
          const [banners, featured, customLists] = this.formatServices(x.services, x.groups);
          params.config.services = {
            ...params.config.services,
            banners,
            featured,
            customLists
          };
        }),
        map(() => void 0)
      );

    return forkJoin([conf$, services$]).pipe(map(() => void 0));
  }

  private setApiCountry(params: GlobalParams): Observable<void> {
    const code = params?.country?.code;
    if (!code || this.loadedCountryConfigs.has(code)) {
      return of(void 0);
    }
    this.loadedCountryConfigs.add(code);

    return this.countryService.getCountryConfig(code).pipe(
      tap((x: ApiCountryConfig) => {
        params.country = {
          ...(params.country ?? {}),
          ...x
        };
      }),
      map(() => void 0)
    );
  }

  private setApiValidations(params: GlobalParams): Observable<void> {
    const code = params?.country?.code;
    if (!code || this.loadedCountryValidations.has(code)) {
      return of(void 0);
    }
    this.loadedCountryValidations.add(code);

    return this.countryService.getValidations(code).pipe(
      tap((x: ValidationsCountry) => {
        params.validations = {
          ...ecommerceConstants.validations,
          ...x
        };
      }),
      map(() => void 0)
    );
  }

  private setOrderConfigs(params: GlobalParams): Observable<void> {
    const code = params?.country?.code;
    if (!code || this.loadedOrderConfigs.has(code)) {
      return of(void 0);
    }
    this.loadedOrderConfigs.add(code);

    return defer(() => {
      this.orderService.addOrderConfig();
      return of(void 0);
    });
  }

  formatServices(servicesList: Service[], groups: Group[]): [Service[], Service[], any[]] {
    const banners: Service[] = [];
    const featured: Service[] = [];
    const groupedMap = new Map<number, { title: string, services: Service[] }>();

    servicesList.forEach((service) => {
      if (service.visibleInBanner) {
        banners.push(service);
      }
      if (service.visibleInFeatured) {
        featured.push(service);
      }

      const groupId = service.group?.id;
      const groupTitle = service.group?.title;
      if (!groupId || !groupTitle){
        return;
      }
      if (!groupedMap.has(groupId)) {
        groupedMap.set(groupId, { title: groupTitle, services: [] });
      }
      groupedMap.get(groupId)?.services.push(service);
    });

    const customLists = groups.map(group => groupedMap.get(group.id)).filter(Boolean);

    return [banners, featured, customLists];
  }

  private applyThemeColors(cfg: ShopConfiguration | null | undefined): void {
    if (!cfg) { return; }
    // SSR-safe: document only exists in the browser. Without this guard, calling from inside
    // a setShopConfiguration tap during SSR threw 'document is not defined', which propagated
    // to setParams' forkJoin and tore down sibling tasks (upper-banner, service/list,
    // country/config, country/validations) — leaving the upper banner missing in the SSR HTML.
    if (!isPlatformBrowser(this.platformId)) { return; }

    const mainNormal = this.asValidColor(cfg.mainColorNormal);
    const mainLight = this.asValidColor(cfg.mainColorLight);
    const mainDark = this.asValidColor(cfg.mainColorDark);
    const secondaryNormal = this.asValidColor(cfg.secondaryColorNormal);
    const secondaryLight = this.asValidColor(cfg.secondaryColorLight);
    const secondaryDark = this.asValidColor(cfg.secondaryColorDark);

    const rootStyle = document.documentElement.style;
    if (mainNormal) {
      rootStyle.setProperty('--sp-color-main', mainNormal);
    }
    if (mainLight) {
      rootStyle.setProperty('--sp-color-main-light', mainLight);
    }
    if (mainDark) {
      rootStyle.setProperty('--sp-color-main-dark', mainDark);
    }
    if (secondaryNormal) {
      rootStyle.setProperty('--sp-color-secondary', secondaryNormal);
    }
    if (secondaryLight) {
      rootStyle.setProperty('--sp-color-secondary-light', secondaryLight);
    }
    if (secondaryDark) {
      rootStyle.setProperty('--sp-color-secondary-dark', secondaryDark);
    }
  }

  private asValidColor(input: string | null | undefined): string | null {
    if (!input || typeof input !== 'string') return null;
    const value = input.trim();

    const isHex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
    const isRgb = /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/.test(value);
    const isRgba = /^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\)$/.test(value);

    if (isHex || isRgb || isRgba) return value;

    const probe = document.createElement('div');
    probe.style.color = '';
    probe.style.color = value;
    if (probe.style.color) return value;

    return null;
  }

  getCountryConfig(countryCode: string) {
    switch (countryCode) {
      case 'ES':
        return countryConfigES;
      case 'GE':
        return countryConfigGE;
      case 'HR':
        return countryConfigHR;
      case 'IT':
        return countryConfigIT;
      case 'KZ':
        return countryConfigKZ;
      case 'MA':
        return countryConfigMA;
      case 'PL':
        return countryConfigPL;
      case 'PT':
        return countryConfigPT;
      case 'RO':
        return countryConfigRO;
      case 'KG':
        return countryConfigKG;
      case 'UA':
        return countryConfigUA;
      case 'RS':
        return countryConfigRS;
      case 'SI':
        return countryConfigSI;
      case 'BG':
        return countryConfigBG;
      case 'BA':
        return countryConfigBA;
      case 'ME':
        return countryConfigME;
      case 'CI':
        return countryConfigCI;
      case 'AD':
        return countryConfigAD;
      case 'AM':
        return countryConfigAM;
      case 'KE':
        return countryConfigKE;
      case 'GH':
        return countryConfigGH;
      case 'MD':
        return countryConfigMD;
      default:
        break;
    }
  }

  getParams(): GlobalParams {
    return this.paramsSubject$.getValue();
  }

  getApiValidations(): ValidationsCountry {
    return this.getParams()?.validations ?? {};
  }

  getShoptConfiguration(): ShopConfiguration {
    return this.getParams()?.shopConfiguration;
}

  translateUrl(
    key: any,
    countryCode?: string,
    locale?: string,
    withoutLocale: boolean = false
  ) {
    const currentParams = this.paramsSubject$.getValue();
    countryCode = countryCode ?? currentParams?.country?.code ?? 'ES';
    locale = locale ?? currentParams?.country?.locale ?? 'es';
    return !withoutLocale
      ? `/${countryCode}/${locale}/${ecommerceConstants.urls[key][countryCode]}`
      : `${ecommerceConstants.urls[key][countryCode]}`;
  }

  translateUrl2(keys: any, withoutLocale: boolean = false, countryCode?: string) {
    let url = '';
    const currentParams = this.paramsSubject$.getValue();
    countryCode = countryCode ?? currentParams.country.code;
    if (!withoutLocale) {
      const country = this.countryService.getCountryByCode(countryCode);
      if (country) {
        url += `/${countryCode}/${country.locale}`;
      }
    }
    keys.forEach((key: any) => {
      url += `/${ecommerceConstants.urls[key] ? ecommerceConstants.urls[key][countryCode] : key}`;
    });
    return url;
  }

  gotoButtonLanding(element: any) {
    if (!isPlatformBrowser(this.platformId)) return;
    switch (element.type) {
      case 'external':
        window.open(element.link, '_blank');
        break;
      case 'internal':
        const top = document.getElementById(element.link).offsetTop;
        const header: any = document.querySelectorAll('.header');
        const submenu: any = document.querySelectorAll('.submenu-row');
        window.scrollTo(0, top - header[0].offsetHeight - submenu[0].offsetHeight);
        break;
      default:
        break;
    }
  }

  getUrlFrontLink(frontLink: FrontLink) {
    let url = '';
    let targetBlank = false;

    switch (frontLink?.frontLinkType) {
      case FrontLinkTypeEnum.page:
        url = this.translateUrl(frontLink.page);
        break;
      case FrontLinkTypeEnum.category:
        url = `${this.translateUrl('category')}/${frontLink.category?.slug}`;
        break;
      case FrontLinkTypeEnum.product:
        if (frontLink.product?.status === ProductStatusEnum.private) {
          url = `${this.translateUrl('product')}/pvt/${frontLink.product?.slug}-${encodeURIComponent(
            frontLink.product?.sku
          )}`;
        } else {
          url = `${this.translateUrl('product')}/${frontLink.product?.slug}-${encodeURIComponent(
            frontLink.product?.sku
          )}`;
        }
        break;
      case FrontLinkTypeEnum.custom:
        url = frontLink.customSlug;
        targetBlank = true;
        break;
      case FrontLinkTypeEnum.collection:
        url = `${this.translateUrl('collection')}/${frontLink.collection.slug}`;
        break;
      case FrontLinkTypeEnum.pack:
        url = `${this.translateUrl('pack')}/${frontLink.pack.slug}`;
        break;
      case FrontLinkTypeEnum.document:
        url = frontLink.document.url;
        targetBlank = true;
        break;
      case FrontLinkTypeEnum.landing:
        url = `${this.translateUrl('landings')}/${frontLink.slug}`;
        break;
      default:
        break;
    }

    return { url, targetBlank };
  }

  goToUrlFrontLink(frontLink: FrontLink) {
    const infoFrontLink = this.getUrlFrontLink(frontLink);
    const url = infoFrontLink.url;
    const targetBlank = infoFrontLink.targetBlank;

    if (
      frontLink.frontLinkType === FrontLinkTypeEnum.custom ||
      frontLink.frontLinkType === FrontLinkTypeEnum.document
    ) {
      if (isPlatformBrowser(this.platformId)) {
        if (targetBlank) {
          window.open(url, '_blank');
        } else {
          window.open(url);
        }
      }
    } else {
      this.router.navigateByUrl(url).then();
    }
  }

  getCustomListProducts(list: StarredProductList): Product[] {
    const link = list.frontLink;
    switch (link?.frontLinkType) {
      case FrontLinkTypeEnum.collection:
        return link.collection?.products ?? [];
      case FrontLinkTypeEnum.category:
        return link.category?.products ?? [];
      default:
        return [];
    }
  }

  getAddressValidations(type: number) {
    const validations = this.getApiValidations();

    // Name
    let nameValidators: ValidatorFn[] = [];
    if (type === CustomerAddressTypeEnum.shipping) {
      nameValidators = [Validators.required, Validators.pattern(ecommerceConstants.validations.spaces)];
    } else if (type === CustomerAddressTypeEnum.billing) {
      nameValidators = [Validators.pattern(ecommerceConstants.validations.spaces)];
    }
    if (validations.minNameChars) {
      nameValidators.push(Validators.minLength(validations.minNameChars));
    }
    if (validations.maxNameChars) {
      nameValidators.push(Validators.maxLength(validations.maxNameChars));
    }
    if (validations.restrictedCharactersName) {
      nameValidators.push(Validators.pattern(this.createRestrictedCharactersPattern(validations.restrictedCharactersName)));
    }

    // Surname
    let surnameValidators: ValidatorFn[] = [];
    if (type === CustomerAddressTypeEnum.shipping) {
      surnameValidators = [Validators.required, Validators.pattern(ecommerceConstants.validations.spaces)];
    } else if (type === CustomerAddressTypeEnum.billing) {
      surnameValidators = [Validators.pattern(ecommerceConstants.validations.spaces)];
    }
    if (validations.minSurnameChars) {
      surnameValidators.push(Validators.minLength(validations.minSurnameChars));
    }
    if (validations.maxSurnameChars) {
      surnameValidators.push(Validators.maxLength(validations.maxSurnameChars));
    }
    if (validations.restrictedCharactersSurname) {
      surnameValidators.push(Validators.pattern(this.createRestrictedCharactersPattern(validations.restrictedCharactersSurname)));
    }

    // BillingName
    let billingNameValidators: ValidatorFn[] = [];
    if (type === CustomerAddressTypeEnum.shipping) {
      billingNameValidators = [Validators.pattern(ecommerceConstants.validations.spaces)];
    } else if (type === CustomerAddressTypeEnum.billing) {
      billingNameValidators = [Validators.required, Validators.pattern(ecommerceConstants.validations.spaces)];
    }

    // Address
    const addressValidators = [
      Validators.required,
      Validators.pattern(ecommerceConstants.validations.spaces)
    ];
    if (validations.minAddressChars) {
      addressValidators.push(Validators.minLength(validations.minAddressChars));
    }
    if (validations.maxAddressChars) {
      addressValidators.push(Validators.maxLength(validations.maxAddressChars));
    }
    if (validations.restrictedCharactersAddress) {
      addressValidators.push(Validators.pattern(this.createRestrictedCharactersPattern(validations.restrictedCharactersAddress)));
    }

    // Address 2
    const address2Validators: ValidatorFn[] = [];
    if (validations.restrictedCharactersAddress) {
      address2Validators.push(Validators.pattern(this.createRestrictedCharactersPattern(validations.restrictedCharactersAddress)));
    }

    // Email
    const emailValidators: ValidatorFn[] = [Validators.required, Validators.email];
    if (validations.minEmailChars) {
      emailValidators.push(Validators.minLength(validations.minEmailChars));
    }
    if (validations.maxEmailChars) {
      emailValidators.push(Validators.maxLength(validations.maxEmailChars));
    }
    if (validations.restrictedCharactersEmail) {
      emailValidators.push(Validators.pattern(this.createRestrictedCharactersPattern(validations.restrictedCharactersEmail)));
    }

    // Phone
    const phoneNumberValidators = [Validators.required, Validators.pattern(ecommerceConstants.validations.spaces)];
    if (validations.minPhoneChars) {
      phoneNumberValidators.push(Validators.minLength(validations.minPhoneChars));
    }
    if (validations.maxPhoneChars) {
      phoneNumberValidators.push(Validators.maxLength(validations.maxPhoneChars));
    }
    if (validations.restrictedCharactersPhoneNumber) {
      phoneNumberValidators.push(Validators.pattern(this.createRestrictedCharactersPattern(validations.restrictedCharactersPhoneNumber)));
    }
    if (validations.phoneRegex) {
      if (typeof validations.phoneRegex === 'string') {
        phoneNumberValidators.push(Validators.pattern(new RegExp(validations.phoneRegex.slice(1, -1))));
      } else {
        phoneNumberValidators.push(Validators.pattern(validations.phoneRegex));
      }
    }

    // City
    const cityValidators = [Validators.required, Validators.pattern(ecommerceConstants.validations.spaces)];
    if (validations.minCityChars) {
      cityValidators.push(Validators.minLength(validations.minCityChars));
    }
    if (validations.maxCityChars) {
      cityValidators.push(Validators.maxLength(validations.maxCityChars));
    }
    if (validations.restrictedCharactersCity) {
      cityValidators.push(Validators.pattern(this.createRestrictedCharactersPattern(validations.restrictedCharactersCity)));
    }

    // PostalCode
    const postalCodeValidators = [Validators.required, Validators.pattern(ecommerceConstants.validations.spaces)];
    if (validations.postalCodeRegex) {
      if (typeof validations.postalCodeRegex === 'string') {
        postalCodeValidators.push(Validators.pattern(new RegExp(validations.postalCodeRegex.slice(1, -1))));
      } else {
        postalCodeValidators.push(Validators.pattern(validations.postalCodeRegex));
      }
    }

    // VAT
    const vatValidators: ValidatorFn[] = [Validators.pattern(ecommerceConstants.validations.spaces)];
    if (type === CustomerAddressTypeEnum.billing) {
      vatValidators.push(Validators.required);
    }
    if (validations.vatRegex) {
      if (typeof validations.vatRegex === 'string') {
        vatValidators.push(Validators.pattern(new RegExp(validations.vatRegex.slice(1, -1))));
      } else {
        vatValidators.push(Validators.pattern(validations.vatRegex));
      }
    }

    // Notes
    const notesValidators: ValidatorFn[] = [];
    if (validations.minOrderNotesChars) {
      notesValidators.push(Validators.minLength(validations.minOrderNotesChars));
    }
    if (validations.maxOrderNotesChars) {
      notesValidators.push(Validators.maxLength(validations.maxOrderNotesChars));
    }
    if (validations.restrictedCharactersNotes) {
      notesValidators.push(Validators.pattern(this.createRestrictedCharactersPattern(validations.restrictedCharactersNotes)));
    }

    // Company
    let companyValidators: ValidatorFn[] = [];
    if (type === CustomerAddressTypeEnum.shipping || type === CustomerAddressTypeEnum.billing) {
      companyValidators = [Validators.pattern(ecommerceConstants.validations.spaces)];
    }

    // Province
    const provinceValidators = [Validators.pattern(ecommerceConstants.validations.spaces)];

    // Type
    const typeValidators = [Validators.required];

    // Country
    const countryValidators = [Validators.required];

    return {
      emailValidators,
      companyValidators,
      nameValidators,
      surnameValidators,
      vatValidators,
      phoneNumberValidators,
      addressValidators,
      address2Validators,
      postalCodeValidators,
      cityValidators,
      provinceValidators,
      typeValidators,
      countryValidators,
      billingNameValidators,
      notesValidators
    };
  }

  createRestrictedCharactersPattern(chars: string): RegExp {
    const escaped = chars.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    return new RegExp(`^[^${escaped}]*$`);
  }

  private resetCountryCaches(): void {
    this.loadedCountryConfigs.clear();
    this.loadedCountryValidations.clear();
    this.loadedCountryApiConfigs.clear();
    this.loadedOrderConfigs.clear();
  }

  logout() {
    this.overlaysService
      .requestWithLoader(() => this.customerService.logout$())
      .pipe(
        finalize(() => {
          this.resetCountryCaches();
        })
      )
      .subscribe({
        next: () => this.router.navigateByUrl(this.translateUrl('login')),
        error: () => {},
      });
  }

  enableMiniCart(loading = true) {
    this.miniCartLoadingSubject.next(loading);
    this.activeMiniCartSubject$.next(true);
  }

  disableMiniCart() {
    this.miniCartLoadingSubject.next(false);
    this.activeMiniCartSubject$.next(false);
  }

  setMiniCartLoading(loading: boolean) {
    this.miniCartLoadingSubject.next(loading);
  }

  inIframe() {
    if (!isPlatformBrowser(this.platformId)) return false;
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  logInfo(title: string, data?: any) {
    if (!environment.production) {
      data ? console.log(title, data) : console.log(title);
    }
  }

  alertInfo(data: any, message: boolean, logged: boolean) {
    if (logged) {
      const customer = this.storageService.getGlobalItem<Customer>('customer');
      if (!customer || customer.emailLogin !== 'sergio.92.hm@gmail.com') {
        return;
      }
    }
    alert(message ? data : JSON.stringify(data));
  }

  private getOrCreateVisibilityObserver(): IntersectionObserver {
    if (!this.visibilityObserver) {
      // rootMargin matches original logic: top < (innerHeight - 100) and bottom >= 200
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const id = '#' + entry.target.id;
            const wasVisible = this.visibilityMap.get(id);
            this.visibilityMap.set(id, entry.isIntersecting);
            if (entry.isIntersecting && !wasVisible) {
              console.log(`[Impression] ${id} entered viewport`);
            }
          }
        },
        { rootMargin: '-200px 0px -100px 0px' }
      );
    }
    return this.visibilityObserver;
  }

  public isVisible(id: string): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;

    if (this.visibilityMap.has(id)) {
      return this.visibilityMap.get(id)!;
    }

    // First call for this id — register with IntersectionObserver
    const element = document.querySelector(id);
    if (element) {
      this.visibilityMap.set(id, false);
      this.getOrCreateVisibilityObserver().observe(element);
    }
    return false;
  }

  public processApiError(error: { message: { [x: string]: any[] } }, showAttrName: boolean = false) {
    let errorMsg = '';
    if (typeof error.message === 'string') {
      return error.message;
    } else {
      const keys = Object.keys(error.message);
      for (const key of keys) {
        if (showAttrName) {
          errorMsg += key + ': ';
        }
        errorMsg += error.message[key].join(', ') + '\n';
      }
    }
    return errorMsg;
  }

  navigateToHome() {
    this.router.navigateByUrl(this.translateUrl('home')).then();
  }

  openCart() {
    this.enableMiniCart(true);
    this.orderService.refreshCartCheck();
  }

  scrollToAnchor(id: string) {
    if (!isPlatformBrowser(this.platformId)) return;
    const yOffset = -300;
    const element = document.getElementById(id);
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  goBack() {
    if (!this.previousUrl || this.previousUrl === '' || this.previousUrl === '/') {
      this.navigateToHome();
    } else if (isPlatformBrowser(this.platformId)) {
      window.history.back();
    }
  }

  getGsUrls() {
    return ecommerceConstants.urls;
  }

  public goToProductInCard(product: Product) {
    const productToUse = product;

    let slugProduct = productToUse?.slug;
    let skuProduct = productToUse?.sku;

    if (productToUse?.master) {
      slugProduct = productToUse.master.slug;
      skuProduct = productToUse.master.sku;

      if (productToUse.uncustomizedProduct) {
        slugProduct = productToUse.uncustomizedProduct.master.slug;
        skuProduct = productToUse.uncustomizedProduct.master.sku;
      }
    } else if (productToUse?.uncustomizedProduct) {
      slugProduct = productToUse.uncustomizedProduct.slug;
      skuProduct = productToUse.uncustomizedProduct.sku;
    }

    const path = `${slugProduct}-${encodeURIComponent(skuProduct)}`;
    this.router.navigateByUrl(`${this.translateUrl('product')}/${path}`).then();
  }

  public onInputCheckText(e: any) {
    let value = e.target.value;
    value = value.replace(ecommerceConstants.validations.invalidCharacters, '');
    e.target.value = value;
  }

  public onKeydownCheckText(e: any) {
    if (ecommerceConstants.validations.invalidCharacters.test(e.keyCode)) {
      e.preventDefault();
    }
  }

  public mustMatch(controlName: string, matchingControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formGroup = control as UntypedFormGroup;
      const controlToMatch = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors['mustMatch']) {
        return null;
      }

      if (controlToMatch.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
        return { mustMatch: true };
      } else {
        matchingControl.setErrors(null);
        return null;
      }
    };
  }
  public isTouchScreendevice() {
    if (!isPlatformBrowser(this.platformId)) return false;
    return 'ontouchstart' in window || navigator.maxTouchPoints;
  }
  public isPrerender(): boolean {
    return isPlatformBrowser(this.platformId) && window.navigator?.userAgent?.includes('Prerender');
  }
  public isServer(): boolean {
    return !isPlatformBrowser(this.platformId);
  }

  public getRemainingDaysOfMonth() {
    const today = new Date();
    const lastDayMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const diff = lastDayMonth.getTime() - today.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }


  public showPaymentMethod(
    paymentMethod: string,
    isByPass = false
  ): boolean {
    const order: Order = this.orderService.currentOrder;
    const countryParams = this.getParams()?.country;
    if (!countryParams?.paymentMethodCountries) {
      return false;
    }

    switch (paymentMethod) {
      case PaymentMethodCodeEnum.card:
        return countryParams?.paymentMethodCountries.some((x) => x.paymentMethod.code === paymentMethod);
      case PaymentMethodCodeEnum.token:
      case PaymentMethodCodeEnum.paypal:
      case PaymentMethodCodeEnum.dotPay:
      case PaymentMethodCodeEnum.cellulant:
      case PaymentMethodCodeEnum.blik:
      case PaymentMethodCodeEnum.mbway:
      case PaymentMethodCodeEnum.applePay:
      case PaymentMethodCodeEnum.googlePay:
        return (
          countryParams?.paymentMethodCountries.some((x) => x.paymentMethod.code === paymentMethod) &&
          !this.inIframe()
        );
      case PaymentMethodCodeEnum.bankTransfer:
        return (
          order?.order?.totalCost >= countryParams?.bankTransferLimitFloor &&
          countryParams?.bankTransferActive
        );
      case PaymentMethodCodeEnum.wallet:
        return countryParams?.paymentMethodCountries.some((x) => x.paymentMethod.code === paymentMethod);
      case PaymentMethodCodeEnum.bypass:
        return isByPass;
      default:
        return false;
    }
  }

  getVariantOptionsGrouped(product: Product): { name: string, options: string[] }[] {
    const variantsMap = new Map<number, { name: string, options: string[] }>();

    for (const slave of product.slavesVariants || []) {
      for (const variant of slave.variants) {
        if (!variantsMap.has(variant.id)) {
          variantsMap.set(variant.id, { name: variant.name, options: [] });
        }

        const variantEntry = variantsMap.get(variant.id);
        for (const opt of variant.options) {
          if (!variantEntry.options.includes(opt.name)) {
            variantEntry.options.push(opt.name);
          }
        }
      }
    }

    return Array.from(variantsMap.values());
  }

  public openModalWalletInformation() {
    this.modalService.open(GlovoWalletModalComponent, {
      windowClass: 'modal-glovo-wallet',
    });
  }

  getActiveIconsForFooter(paymentMethodCountries: PaymentMethodCountry[] | null | undefined): PaymentIcon[] {
    return this.getActiveIcons(paymentMethodCountries, this.PAYMENT_ICON_MAP_FOOTER);
  }

  getActiveIconsForCart(paymentMethodCountries: PaymentMethodCountry[] | null | undefined): PaymentIcon[] {
    return this.getActiveIcons(paymentMethodCountries, this.PAYMENT_ICON_MAP_CART);
  }

  private getActiveIcons(
    paymentMethodCountries: PaymentMethodCountry[] | null | undefined,
    iconMap: Map<PaymentMethodCodeEnum, { srcs: string[]; alt: string, width: string, height: string }>
  ): PaymentIcon[] {
    const enabledCodes: PaymentMethodCodeEnum[] = (paymentMethodCountries ?? [])
      .map(pm => pm?.paymentMethod?.code as PaymentMethodCodeEnum)
      .filter((code): code is PaymentMethodCodeEnum => !!code)
      .filter(code => this.showPaymentMethod(code));

    const hasCard = enabledCodes.includes(PaymentMethodCodeEnum.card);
    const hasToken = enabledCodes.includes(PaymentMethodCodeEnum.token);
    const filteredCodes = hasCard && hasToken
      ? enabledCodes.filter(code => code !== PaymentMethodCodeEnum.token)
      : enabledCodes;

    const icons: PaymentIcon[] = [];
    for (const [code, def] of iconMap) {
      if (!filteredCodes.includes(code)) continue;
      def.srcs.forEach(src => icons.push({ src, alt: def.alt, width: def.width, height: def.height }));
    }

    return icons;
  }
}
